import { ChangeDetectorRef, OnDestroy, Pipe, PipeTransform } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";

@Pipe({
    name: "orderBy",
    standalone: true,
    pure: false
})
export class OrderByPipe implements PipeTransform, OnDestroy {
    sortedArray: Array<object> = [];
    originalArray: Array<object> | null = null;
    private langueSub: any = null;

    constructor(private readonly translateService: TranslateService, private _ref: ChangeDetectorRef) { }

    _dispose(): void {
        if (this.langueSub !== null) {
            this.langueSub.unsubscribe();
            this.langueSub = null;
        }
    }

    ngOnDestroy(): void {
        this._dispose();
    }

    private doPropComparison(a: object, b: object, idx: number, propPaths: string[][], translateLasts: boolean[], translateSpecials: (boolean | string)[]): number {
        let aV = a;
        let bV = b;

        const trSpe = translateSpecials[idx];
        if (typeof (trSpe) === 'string') {
            const subPropPath = trSpe.split('.');
            let pA = aV;
            let pB = bV;
            while (subPropPath.length > 0) {
                const p = subPropPath.shift();
                pA = pA[p];
                pB = pB[p];
            }

            const isA = Number(pA) < 0;
            const isB = Number(pB) < 0;
            if (isA && isB)
                return 0;
            else if (isA)
                return -1;
            else if (isB)
                return 1;
        }

        for (let i = 0; i < propPaths[idx].length; i++) {
            const prop = propPaths[idx][i];
            aV = aV[prop];
            bV = bV[prop];

            if (!translateLasts[idx] || i < propPaths[idx].length - 1)
                continue;

            if (typeof (aV) !== 'string' || typeof (bV) !== 'string')
                throw new Error('The translate pipe may only affect string properties.');

            aV = this.translateService.instant(aV);
            bV = this.translateService.instant(bV);
        }

        if (aV < bV)
            return -1;
        else if (aV > bV)
            return 1;
        else
            return (idx < propPaths.length - 1) ? this.doPropComparison(a, b, idx + 1, propPaths, translateLasts, translateSpecials) : 0;
    }

    transform(array: Array<object>, field: string): Array<object> {
        if (!Array.isArray(array)) {
            throw new Error('Only an array may be passed to the orderBy pipe.');
        }

        const separateFields = field.split(';').map(f => f.trim());
        const propPaths: string[][] = [];
        const translateLasts: boolean[] = [];
        const translateSpecials: (boolean | string)[] = [];

        for (let i = 0; i < separateFields.length; i++) {
            const separateField = separateFields[i];

            propPaths[i] = separateField.split('.');
            translateLasts[i] = false;
            translateSpecials[i] = false;

            const potentialPipe = propPaths[i][propPaths[i].length - 1].split('|');
            if (potentialPipe.length > 2) {
                throw new Error('Only one pipe may be used.');
            }

            if (potentialPipe.length == 2) {
                const pipe = potentialPipe[1].trim();
                const pipeL = pipe.toLowerCase();
                if (pipeL == 'translate')
                    translateLasts[i] = true;
                else if (pipeL.startsWith('translates(') && pipeL.endsWith(')')) {
                    translateLasts[i] = true;
                    translateSpecials[i] = pipe.substring('translates('.length, pipe.length-1);
                }
                else
                    throw new Error('Unsupported pipe: ' + potentialPipe[1].trim());

                propPaths[i][propPaths[i].length - 1] = potentialPipe[0].trim();
            }
        }

        this.sortedArray = [...array];
        this.sortedArray.sort((a: object, b: object) => this.doPropComparison(a, b, 0, propPaths, translateLasts, translateSpecials));

        this._dispose();

        if (translateLasts.findIndex(tL => tL) >= 0 && this.langueSub === null) {
            this.langueSub = this.translateService.onLangChange.subscribe(langue => {
                if (langue.lang != null) {
                    this._ref.markForCheck();
                    this.transform(array, field);
                }
            });
        }

        this.originalArray = array;
        return this.sortedArray;
    }
}
